import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowLeft, ShoppingBag, Lock, User, Globe } from "lucide-react";
import { DahliasLogo } from "../brand/Logos";
import { useCart } from "../../store/CartContext";
import { useLang } from "../../i18n/LanguageContext";
import { useSiteSettings, useSiteTheme } from "../../hooks/siteSettings";
import api from "../../store/api";

const NAV = [
  { to: "/dahlias", k: "home", slug: "home", end: true },
  { to: "/dahlias/store", k: "store", slug: "store" },
  { to: "/dahlias/catalog", k: "catalog", slug: "catalog" },
  { to: "/crush", k: "crushClub", slug: "crush-club" },
  { to: "/dahlias/mystery-boxes", k: "mysteryBoxes", slug: "mystery-boxes" },
  { to: "/dahlias/private-pickings", k: "privatePickings", slug: "private-pickings" },
  { to: "/fairitage", k: "fairitage", slug: "fairitage" },
  { to: "/dahlias/about", k: "ourFarm", slug: "our-farm" },
  { to: "/breeders-garden", k: "breedersClub", slug: "breeders-club" },
  { to: "/bloom-club", k: "bloomClub", slug: "bloom-club" },
];

/**
 * Resolve identity from the same authenticated endpoint used by checkout.
 * This keeps the public header, store and HQ in one truthful session state.
 */
function useHqIdentity() {
  const [me, setMe] = useState(null);
  useEffect(() => {
    let alive = true;
    const read = async () => {
      // Signed-out visitors have no token , skip the call instead of logging a 401.
      let hasToken = false;
      try { hasToken = !!window.localStorage?.getItem("heritage-customer-token"); } catch { /* storage unavailable */ }
      if (!hasToken) { if (alive) setMe(null); return; }
      try {
        const response = await api.get("/customer/me");
        if (alive) setMe(response.data || null);
      } catch {
        if (alive) setMe(null);
        try { window.localStorage?.removeItem("heritage-customer-token"); } catch { /* storage unavailable */ }
      }
    };
    read();
    const onStorage = (e) => { if (!e.key || e.key === "heritage-customer-token") read(); };
    const onSession = () => read();
    window.addEventListener("storage", onStorage);
    window.addEventListener("heritage-customer-session", onSession);
    return () => {
      alive = false;
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("heritage-customer-session", onSession);
    };
  }, []);
  return me;
}

function MyHqLink({ variant = "desktop", onNavigate }) {
  const me = useHqIdentity();
  const { t } = useLang();
  const to = me ? "/account" : "/account/login";
  const first = (me?.name || me?.email || "").split(/[\s@]/)[0];
  const initial = first ? first[0].toUpperCase() : "";
  if (variant === "mobile") {
    return (
      <NavLink
        to={to}
        onClick={onNavigate}
        data-testid="nav-my-hq-mobile"
        className="block px-5 py-5 font-display text-2xl border-b border-[var(--dahlias-sage)]/10 text-[var(--dahlias-ink)]/85"
      >
        {me
          ? <><User className="inline w-5 h-5 mr-2" /> {t("nav.hi")} {first || t("nav.there")}, {t("nav.openHq")}</>
          : <><Lock className="inline w-5 h-5 mr-2" /> {t("nav.myHq")} , {t("nav.signIn")}</>}
      </NavLink>
    );
  }
  if (variant === "icon") {
    return (
      <Link to={to} onClick={onNavigate} data-testid="dahlias-account-link-mobile" aria-label={me ? "Open My HQ" : "Sign in to Heritage HQ"}
        className="lg:hidden relative w-10 h-10 inline-flex items-center justify-center rounded-full border border-[var(--dahlias-sage)]/30 text-[var(--dahlias-ink)] hover:bg-[var(--dahlias-sage)]/10">
        {me ? <span className="w-7 h-7 inline-flex items-center justify-center rounded-full bg-[var(--dahlias-sage-deep)] text-[var(--dahlias-cream)] text-xs font-semibold">{initial}</span> : <User className="w-5 h-5" />}
        <span className="sr-only">{me ? "My HQ" : "Sign in"}</span>
      </Link>
    );
  }
  return (
    <Link
      to={to}
      data-testid="dahlias-account-link"
      className="hidden lg:inline-flex items-center gap-2 px-3 py-2 border border-[var(--dahlias-sage)]/30 hover:border-[var(--dahlias-sage-deep)] hover:bg-[var(--dahlias-sage)]/10 rounded-full text-sm transition"
    >
      {me ? (
        <>
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[var(--dahlias-sage-deep)] text-[var(--dahlias-cream)] text-[11px] font-semibold">{initial}</span>
          <span className="whitespace-nowrap">{first || t("nav.myHq")}</span>
        </>
      ) : (
        <>
          <Lock className="w-4 h-4" />
          {t("nav.myHq")}
        </>
      )}
    </Link>
  );
}

export default function DahliasShell({ children, hideStartLink = false }) {
  const [open, setOpen] = useState(false);
  const { totalQty } = useCart();
  const { lang, setLang, t } = useLang();
  const toggleLang = () => setLang(lang === "en" ? "mi" : "en");
  const settings = useSiteSettings();
  useSiteTheme(settings);
  const labelFor = (n) => settings?.nav_labels?.[n.slug] || t(`nav.${n.k}`);
  const sales = settings?.sales || {};
  const nav = NAV.filter((n) => (settings?.nav?.[n.slug]) !== false)
    .filter((n) => !(n.slug === "store" && (sales.paused || sales.hide_store)) && !(n.slug === "catalog" && sales.hide_catalogue));

  return (
    <div data-testid="dahlias-shell" className="dahlias-site min-h-[100svh] flex flex-col bg-[var(--dahlias-cream)] text-[var(--dahlias-ink)]">
      {/* Sage utility strip, quiet, badge-coloured */}
      <div className="bg-[var(--dahlias-sage)] text-[var(--dahlias-cream)]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-2 flex items-center justify-between text-[11px] md:text-xs font-label tracking-[0.35em]">
          <span className="hidden sm:inline">AOTEAROA</span>
          <span>{t("strip.familyGrown")}</span>
          <span className="flex items-center gap-3">
            <span className="hidden sm:inline">EST. 1932</span>
            <button
              type="button"
              onClick={toggleLang}
              data-testid="lang-toggle-public"
              aria-label={lang === "en" ? "Switch to Te Reo Māori" : "Switch to English"}
              className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border border-[var(--dahlias-cream)]/40 hover:bg-white/10 transition text-[10px] tracking-[0.2em]"
            >
              <Globe className="w-3 h-3" /> {lang === "en" ? "TE REO" : "ENGLISH"}
            </button>
          </span>
        </div>
      </div>

      {/* Header */}
      <header className="border-b border-[var(--dahlias-sage)]/15 bg-[var(--dahlias-paper)] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-5 md:px-8 py-3 md:py-4 flex items-center gap-2 sm:gap-4">
          {!hideStartLink ? (
            <Link
              to="/"
              data-testid="dahlias-back-start"
              className="hidden md:inline-flex items-center gap-1 text-sm text-[var(--dahlias-ink)]/70 hover:text-[var(--dahlias-sage-deep)] transition"
            >
              <ArrowLeft className="w-4 h-4" /> {t("nav.start")}
            </Link>
          ) : (
            <span className="hidden md:block w-16" />
          )}

          <Link to="/" data-testid="dahlias-logo-link" aria-label="Heritage home" className="flex items-center gap-2 sm:gap-3 mr-auto lg:mr-0">
            <span className="font-display tracking-[0.14em] sm:tracking-[0.22em] uppercase text-base sm:text-xl md:text-2xl text-[var(--dahlias-forest)] leading-none whitespace-nowrap">
              HERITAGE DAHLIAS
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-5 2xl:gap-8">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.slug}`}
                className={({ isActive }) =>
                  `font-display text-base xl:text-lg whitespace-nowrap transition ${
                    isActive
                      ? "text-[var(--dahlias-sage-deep)] font-semibold underline underline-offset-8 decoration-[var(--dahlias-clay)] decoration-2"
                      : "text-[var(--dahlias-ink)]/85 hover:text-[var(--dahlias-clay)]"
                  }`
                }
              >
                {labelFor(n)}
              </NavLink>
            ))}
          </nav>

          <Link to="/cart" data-testid="dahlias-cart-link-mobile" aria-label={`Cart with ${totalQty} item${totalQty === 1 ? "" : "s"}`}
            className="lg:hidden relative w-10 h-10 inline-flex items-center justify-center rounded-full border border-[var(--dahlias-sage)]/30 text-[var(--dahlias-ink)] hover:bg-[var(--dahlias-sage)]/10">
            <ShoppingBag className="w-5 h-5" />
            {totalQty > 0 && <span data-testid="dahlias-cart-count-mobile" className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[var(--dahlias-clay)] text-white text-[10px] font-semibold grid place-items-center">{totalQty}</span>}
          </Link>
          <MyHqLink variant="icon" />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="dahlias-mobile-menu-btn"
            className="xl:hidden p-2 text-[var(--dahlias-ink)]"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          <Link
            to="/cart"
            data-testid="dahlias-cart-link"
            className="hidden lg:inline-flex items-center gap-2 px-4 py-2 border border-[var(--dahlias-sage)]/30 hover:border-[var(--dahlias-sage-deep)] hover:bg-[var(--dahlias-sage)]/10 rounded-full text-sm transition"
          >
            <ShoppingBag className="w-4 h-4" />
            {t("nav.cart")}{totalQty ? ` (${totalQty})` : ""}
          </Link>
          <MyHqLink />
        </div>
        {open && (
          <nav data-testid="dahlias-mobile-nav" className="xl:hidden border-t border-[var(--dahlias-sage)]/15 bg-[var(--dahlias-paper)]">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-5 py-5 font-display text-2xl border-b border-[var(--dahlias-sage)]/10 ${
                    isActive
                      ? "text-[var(--dahlias-clay)] font-semibold bg-[var(--dahlias-cream)]"
                      : "text-[var(--dahlias-ink)]/85"
                  }`
                }
              >
                {labelFor(n)}
              </NavLink>
            ))}
            <NavLink to="/cart" onClick={() => setOpen(false)} data-testid="nav-cart-mobile"
              className="block px-5 py-5 font-display text-2xl border-b border-[var(--dahlias-sage)]/10 text-[var(--dahlias-ink)]/85">
              <ShoppingBag className="inline w-5 h-5 mr-2" /> {t("nav.cart")}{totalQty ? ` (${totalQty})` : ""}
            </NavLink>
            <MyHqLink variant="mobile" onNavigate={() => setOpen(false)} />
          </nav>
        )}
      </header>

      {/* Page content */}
      <main className="flex-1">{children}</main>

      {/* Footer, sage band with scalloped top */}
      <div aria-hidden="true" className="h-7 bg-[var(--dahlias-sage)] scallop-top" />
      <footer
        data-testid="dahlias-footer"
        className="bg-[var(--dahlias-sage)] text-[var(--dahlias-cream)]"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 md:py-16 grid grid-cols-1 md:grid-cols-3 gap-10 items-start">
          <div className="flex flex-col items-start gap-5">
            <DahliasLogo variant="sage" className="w-24 h-24" />
            <p className="text-sm text-[var(--dahlias-cream)]/85 max-w-xs leading-relaxed">
              {t("footer.tagline")}
            </p>
          </div>

          <div className="text-sm space-y-3">
            <div className="font-label tracking-[0.35em] text-[11px] text-[var(--dahlias-cream)]/70">
              {t("footer.pages")}
            </div>
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="block text-[var(--dahlias-cream)]/90 hover:text-white transition"
              >
                {labelFor(n)}
              </Link>
            ))}
          </div>

          <div className="text-sm space-y-3">
            <div className="font-label tracking-[0.35em] text-[11px] text-[var(--dahlias-cream)]/70">
              {t("footer.getInTouch")}
            </div>
            <a
              href="mailto:heritagedahliasnz@gmail.com"
              className="block hover:text-white transition"
            >
              heritagedahliasnz@gmail.com
            </a>
            <Link to="/dahlias/about" className="block hover:text-white transition">
              {t("footer.sendMessage")}
            </Link>
            <Link
              to="/"
              className="block mt-4 text-[var(--dahlias-cream)]/70 hover:text-white transition"
            >
              {t("footer.startPage")}
            </Link>
          </div>
        </div>
        <div className="border-t border-[var(--dahlias-cream)]/15 px-5 md:px-8 py-5 text-center text-xs text-[var(--dahlias-cream)]/65 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} Heritage Dahlias · {t("footer.legacy")}</span>
          <span className="flex items-center gap-1 flex-wrap justify-center">
            <Link to="/legal/privacy" className="px-2 hover:text-white transition">{t("footer.privacy")}</Link>
            <span aria-hidden="true">·</span>
            <Link to="/legal/terms" className="px-2 hover:text-white transition">{t("footer.terms")}</Link>
            <span aria-hidden="true">·</span>
            <Link to="/legal/shipping" className="px-2 hover:text-white transition">{t("footer.shipping")}</Link>
            <span aria-hidden="true">·</span>
            <Link to="/legal/returns" className="px-2 hover:text-white transition">{t("footer.returns")}</Link>
            <span aria-hidden="true">·</span>
            <Link to="/legal/ip" className="px-2 hover:text-white transition">{t("footer.ip")}</Link>
            <span aria-hidden="true">·</span>
            <Link to="/legal/grower-agreement" className="px-2 hover:text-white transition">{t("footer.grower")}</Link>
          </span>
        </div>
      </footer>
    </div>
  );
}
